// jsPDF-based printable report for the AI Worksheet Marker.
// 16-point Times-style body text on A4 portrait pages.
import { jsPDF } from "jspdf";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 18;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const BODY_SIZE = 16;
const LINE_HEIGHT = 7.2;

function safeText(value, fallback = "Not provided") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function fileSafe(value) {
  return safeText(value, "worksheet-marking")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function downloadMarkingPdf(result, metadata) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  doc.setProperties({
    title: `${safeText(result.worksheetTitle, "English Worksheet")} - Marking Report`,
    subject: "AI-assisted English worksheet marking report",
    author: "Prof.Scaly",
  });

  let y = MARGIN;
  let pageNumber = 1;

  const addFooter = () => {
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.text(`Prof.Scaly AI-assisted marking | Page ${pageNumber}`, PAGE_WIDTH / 2, PAGE_HEIGHT - 8, {
      align: "center",
    });
  };

  const newPage = () => {
    addFooter();
    doc.addPage();
    pageNumber += 1;
    y = MARGIN;
  };

  const ensure = (needed) => {
    if (y + needed > PAGE_HEIGHT - 16) newPage();
  };

  const heading = (text, size = 20) => {
    ensure(size * 0.6 + 5);
    doc.setFont("times", "bold");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(safeText(text), CONTENT_WIDTH);
    doc.text(lines, MARGIN, y);
    y += lines.length * (size * 0.42) + 3;
  };

  const paragraph = (text, options = {}) => {
    const { bold = false, indent = 0, spaceAfter = 3 } = options;
    doc.setFont("times", bold ? "bold" : "normal");
    doc.setFontSize(BODY_SIZE);
    const width = CONTENT_WIDTH - indent;
    const lines = doc.splitTextToSize(safeText(text, ""), width);
    const needed = Math.max(LINE_HEIGHT, lines.length * LINE_HEIGHT) + spaceAfter;
    ensure(needed);
    doc.text(lines, MARGIN + indent, y);
    y += lines.length * LINE_HEIGHT + spaceAfter;
  };

  const labelValue = (label, value) => {
    paragraph(`${label}: ${safeText(value)}`, { spaceAfter: 2 });
  };

  heading("Prof.Scaly English Worksheet Marking Report", 22);
  paragraph("AI-assisted draft for tutor review", { bold: true, spaceAfter: 5 });
  labelValue("Student", metadata.studentName || "Initials/pseudonym not provided");
  labelValue("Primary level", metadata.level);
  labelValue("Worksheet type", result.worksheetType || metadata.worksheetType);
  labelValue("Worksheet title", result.worksheetTitle);
  labelValue("Scan quality", result.scanQuality);
  paragraph(
    `Score: ${result.totalScore}/${result.totalPossible} (${Math.round(result.percentage)}%)`,
    { bold: true, spaceAfter: 5 },
  );

  if (result.reviewRequired) {
    paragraph("Tutor review required: one or more answers were unclear or marked with low confidence.", {
      bold: true,
      spaceAfter: 5,
    });
  }

  heading("Question-by-question marking", 19);
  result.questions.forEach((question) => {
    ensure(42);
    doc.setDrawColor(150);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 4;
    paragraph(`Question ${question.number}`, { bold: true, spaceAfter: 2 });
    if (question.questionText) paragraph(`Question: ${question.questionText}`, { spaceAfter: 2 });
    paragraph(`Student's answer: ${safeText(question.studentAnswer, "[No answer detected]")}`, {
      spaceAfter: 2,
    });
    paragraph(`Correct answer: ${safeText(question.correctAnswer, "Tutor review required")}`, {
      spaceAfter: 2,
    });
    paragraph(
      `Result: ${String(question.status).toUpperCase()} | Marks: ${question.marksAwarded}/${question.marksPossible} | Confidence: ${question.confidence}`,
      { bold: true, spaceAfter: 2 },
    );
    paragraph(`Explanation: ${question.explanation}`, { spaceAfter: 4 });
  });

  heading("Teacher feedback", 19);
  paragraph(result.teacherFeedback);

  if (result.strengths?.length) {
    heading("Strengths", 19);
    result.strengths.forEach((item) => paragraph(`- ${item}`, { indent: 3, spaceAfter: 2 }));
  }

  if (result.improvementAreas?.length) {
    heading("Areas for improvement", 19);
    result.improvementAreas.forEach((item) => paragraph(`- ${item}`, { indent: 3, spaceAfter: 2 }));
  }

  if (result.unclearRegions?.length) {
    heading("Items requiring tutor checking", 19);
    result.unclearRegions.forEach((item) => paragraph(`- ${item}`, { indent: 3, spaceAfter: 2 }));
  }

  heading("Important note", 18);
  paragraph(
    "This report was generated with AI assistance. The tutor should verify handwriting, answer keys, marks and feedback before sharing it with a student or parent.",
  );

  addFooter();
  const filename = `${fileSafe(metadata.studentName || "student")}-${fileSafe(result.worksheetTitle)}-marking-report.pdf`;
  doc.save(filename);
}
